import type { CSSProperties, ReactNode } from 'react';
import './ui.css';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  width?: string;
  align?: 'left' | 'right';
}

interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
}

function cellStyle<T>(col: Column<T>): CSSProperties | undefined {
  const style: CSSProperties = {};
  if (col.width) style.width = col.width;
  if (col.align === 'right') style.textAlign = 'right';
  return Object.keys(style).length > 0 ? style : undefined;
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  empty = 'Nothing here yet.',
  onRowClick,
}: TableProps<T>) {
  return (
    <div className="card ui-table-wrap">
      <table className="ui-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={cellStyle(col)}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="ui-table-empty">
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                className={onRowClick ? 'ui-row-click' : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => (
                  <td key={col.key} style={cellStyle(col)}>
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
